import { CSSProperties, MutableRefObject, RefObject } from 'react';
import {
  calculateStickyHeaderLayoutState,
  isZeroSizedRect,
  TransitionMetrics,
} from './stickyRecipeHeader.helpers';

export interface StickyHeaderStateSetters {
  setIsStickyRecipeHeaderVisible: (value: boolean) => void;
  setStickyHeaderProgress: (value: number) => void;
  setIsSharedPhotoVisible: (value: boolean) => void;
  setSharedPhotoStyle: (value: CSSProperties | null) => void;
}

interface ScrollController {
  getCurrentScrollTop: () => number;
  setCurrentScrollTop: (value: number) => void;
}

interface StickyHeaderUpdaterOptions {
  imageWrapperRef: RefObject<HTMLDivElement | null>;
  tapedPhotoEffectRef: RefObject<HTMLDivElement | null>;
  stickyImageButtonRef: RefObject<HTMLButtonElement | null>;
  scrollContainer: HTMLElement | null;
  transitionStartOffset: number;
  snapCompletionOffset: number;
  setters: StickyHeaderStateSetters;
  transitionMetricsRef: MutableRefObject<TransitionMetrics | null>;
  scrollController: ScrollController;
}

interface StickyHeaderAnimationOptions {
  targetScrollTop: number;
  autoSnapFrameRef: MutableRefObject<number | null>;
  autoSnapActiveRef: MutableRefObject<boolean>;
  stopAutoSnap: () => void;
  getCurrentScrollTop: () => number;
  setCurrentScrollTop: (value: number) => void;
  updateStickyHeaderVisibility: () => void;
}

interface StickyHeaderSettleGuardOptions {
  autoSnapActiveRef: MutableRefObject<boolean>;
  isTouchScrollActiveRef: MutableRefObject<boolean>;
  transitionMetricsRef: MutableRefObject<TransitionMetrics | null>;
}

interface StickyHeaderSettleScheduleOptions {
  settleTimeoutRef: MutableRefObject<number | null>;
  clearSettleTimeout: () => void;
  settleScrollIfNeeded: () => void;
  shouldCompleteOngoingTransition: boolean;
}

export const resetStickyHeaderState = (
  setters: StickyHeaderStateSetters,
  transitionMetricsRef: MutableRefObject<TransitionMetrics | null>
) => {
  setters.setIsStickyRecipeHeaderVisible(false);
  setters.setStickyHeaderProgress(0);
  setters.setIsSharedPhotoVisible(false);
  setters.setSharedPhotoStyle(null);
  transitionMetricsRef.current = null;
};

export const clearStickySettleTimeout = (
  settleTimeoutRef: MutableRefObject<number | null>
) => {
  if (settleTimeoutRef.current !== null) {
    window.clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = null;
  }
};

export const stopStickyAutoSnap = ({
  autoSnapFrameRef,
  autoSnapActiveRef,
}: {
  autoSnapFrameRef: MutableRefObject<number | null>;
  autoSnapActiveRef: MutableRefObject<boolean>;
}) => {
  if (autoSnapFrameRef.current !== null) {
    window.cancelAnimationFrame(autoSnapFrameRef.current);
    autoSnapFrameRef.current = null;
  }

  autoSnapActiveRef.current = false;
};

export const canSettleStickyHeader = ({
  autoSnapActiveRef,
  isTouchScrollActiveRef,
  transitionMetricsRef,
}: StickyHeaderSettleGuardOptions): TransitionMetrics | null => {
  if (autoSnapActiveRef.current || isTouchScrollActiveRef.current) {
    return null;
  }

  const metrics = transitionMetricsRef.current;
  if (!metrics || metrics.progress <= 0 || metrics.progress >= 1) {
    return null;
  }

  return metrics;
};

export const scheduleStickySettleTimeout = ({
  settleTimeoutRef,
  clearSettleTimeout,
  settleScrollIfNeeded,
  shouldCompleteOngoingTransition,
}: StickyHeaderSettleScheduleOptions) => {
  clearSettleTimeout();
  settleTimeoutRef.current = window.setTimeout(
    settleScrollIfNeeded,
    shouldCompleteOngoingTransition ? 0 : 120
  );
};

export const createScrollController = (scrollContainer: HTMLElement | null): ScrollController => ({
  getCurrentScrollTop: () =>
    scrollContainer instanceof HTMLElement ? scrollContainer.scrollTop : window.scrollY,
  setCurrentScrollTop: (value: number) => {
    if (scrollContainer instanceof HTMLElement) {
      scrollContainer.scrollTo({ top: value, behavior: 'auto' });
      return;
    }

    window.scrollTo({ top: value, behavior: 'auto' });
  },
});

export const createStickyHeaderUpdater = ({
  imageWrapperRef,
  tapedPhotoEffectRef,
  stickyImageButtonRef,
  scrollContainer,
  transitionStartOffset,
  snapCompletionOffset,
  setters,
  transitionMetricsRef,
  scrollController,
}: StickyHeaderUpdaterOptions) => () => {
  const wrapper = imageWrapperRef.current;
  const sourcePhoto = tapedPhotoEffectRef.current;
  const stickyButton = stickyImageButtonRef.current;

  if (!wrapper || !sourcePhoto || !stickyButton) {
    resetStickyHeaderState(setters, transitionMetricsRef);
    return;
  }

  const rect = wrapper.getBoundingClientRect();
  if (isZeroSizedRect(rect)) {
    resetStickyHeaderState(setters, transitionMetricsRef);
    return;
  }

  const sourceRect = sourcePhoto.getBoundingClientRect();
  const targetRect = stickyButton.getBoundingClientRect();
  const scrollContainerRect =
    scrollContainer instanceof HTMLElement
      ? scrollContainer.getBoundingClientRect()
      : { top: 0 };
  const currentScrollTop = scrollController.getCurrentScrollTop();
  const nextLayoutState = calculateStickyHeaderLayoutState({
    wrapperRect: rect,
    sourceRect,
    sourceWidth: sourcePhoto.offsetWidth,
    sourceHeight: sourcePhoto.offsetHeight,
    targetRect,
    currentScrollTop,
    scrollContainerTop:
      scrollContainer instanceof HTMLElement ? scrollContainerRect.top : 0,
    transitionStartOffset,
    snapCompletionOffset,
  });

  setters.setIsStickyRecipeHeaderVisible(nextLayoutState.isStickyRecipeHeaderVisible);
  setters.setStickyHeaderProgress(nextLayoutState.progress);
  setters.setIsSharedPhotoVisible(nextLayoutState.isSharedPhotoVisible);
  transitionMetricsRef.current = nextLayoutState.transitionMetrics;
  setters.setSharedPhotoStyle(nextLayoutState.sharedPhotoStyle);
};

export const animateStickyHeaderScroll = ({
  targetScrollTop,
  autoSnapFrameRef,
  autoSnapActiveRef,
  stopAutoSnap,
  getCurrentScrollTop,
  setCurrentScrollTop,
  updateStickyHeaderVisibility,
}: StickyHeaderAnimationOptions) => {
  stopAutoSnap();

  const startScrollTop = getCurrentScrollTop();
  if (Math.abs(targetScrollTop - startScrollTop) < 1) {
    return;
  }

  autoSnapActiveRef.current = true;
  const duration = 140;
  const startTime = performance.now();
  const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);

  const step = (timestamp: number) => {
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);
    const nextScrollTop =
      startScrollTop + (targetScrollTop - startScrollTop) * easedProgress;

    setCurrentScrollTop(nextScrollTop);
    updateStickyHeaderVisibility();

    if (progress < 1) {
      autoSnapFrameRef.current = window.requestAnimationFrame(step);
      return;
    }

    autoSnapActiveRef.current = false;
    autoSnapFrameRef.current = null;
  };

  autoSnapFrameRef.current = window.requestAnimationFrame(step);
};
